'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

const AboutSection: React.FC = () => {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const h = t.home;
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const aboutTitle = language === 'fr' ? 'Qui sommes-nous' : 'Quiénes somos';
  const aboutSubtitle = language === 'fr' ? 'Nous construisons l\'avenir numérique du Chili' : 'Construimos el futuro digital de Chile';
  const ctaPresentation = language === 'fr' ? 'Télécharger Présentation' : 'Descargar Presentación';
  const ctaMore = language === 'fr' ? 'Connaître Plus' : 'Conocer Más';

  return (
    <section
      id="nosotros"
      ref={sectionRef}
      className="relative overflow-hidden"
      style={{ backgroundColor: '#141618', paddingTop: '120px', paddingBottom: '120px' }}>
      
      {/* Grid lines */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-white/5" />
        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-white/5" />
        <div className="absolute left-0 right-0 top-0 h-px bg-white/6" />
        <div className="absolute left-0 right-0 bottom-0 h-px bg-white/6" />
      </div>

      {/* Accent dot */}
      <div
        className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(171,210,51,0.08) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)'
        }} />
      

      <div className="max-w-[1280px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* Left: Image */}
          <div
            className={`relative transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`
            }>
            {/* Frame with corner dots */}
            <div className="relative">
              {/* Corner dots */}
              <div className="absolute -top-[5px] -left-[5px] w-[10px] h-[10px] rounded-full bg-[#abd233] z-10" />
              <div className="absolute -top-[5px] -right-[5px] w-[10px] h-[10px] rounded-full bg-white/20 z-10" />
              <div className="absolute -bottom-[5px] -left-[5px] w-[10px] h-[10px] rounded-full bg-white/20 z-10" />
              <div className="absolute -bottom-[5px] -right-[5px] w-[10px] h-[10px] rounded-full bg-[#abd233] z-10" />
              {/* Border lines */}
              <div className="absolute top-0 left-0 right-0 h-px bg-white/15" />
              <div className="absolute bottom-0 left-0 right-0 h-px bg-white/15" />
              <div className="absolute top-0 bottom-0 left-0 w-px bg-white/15" />
              <div className="absolute top-0 bottom-0 right-0 w-px bg-white/15" />
              {/* Diagonal line */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ opacity: 0.1 }}>
                <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                  <line x1="0" y1="100" x2="100" y2="0" stroke="white" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                </svg>
              </div>
              <img
                src="https://innobox.cl/wp-content/uploads/2021/06/About-1st-Image.png"
                alt="Equipo Innobox trabajando en soluciones tecnológicas"
                className="w-full h-auto block"
                style={{ aspectRatio: '4/3', objectFit: 'cover' }} />
              
            </div>
            {/* Floating stat card */}
            <div
              className="absolute -bottom-6 -right-6 hidden lg:block"
              style={{
                backgroundColor: '#202427',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '20px 24px',
                minWidth: '160px'
              }}>
              <div className="text-[#abd233]" style={{ fontSize: '32px', fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1 }}>10+</div>
              <div className="text-white/50 mt-1" style={{ fontSize: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                {language === 'fr' ? 'Années d\'expérience' : 'Años de experiencia'}
              </div>
            </div>
          </div>

          {/* Right: Text */}
          <div
            className={`transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`
            }>
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-6 h-px bg-[#abd233]" />
              <span style={{ fontSize: '12px', letterSpacing: '0.1em', color: '#abd233', textTransform: 'uppercase' }}>
                {aboutTitle}
              </span>
            </div>

            <h2
              className="text-white mb-6"
              style={{
                fontSize: 'clamp(32px, 3.5vw, 52px)',
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.01em'
              }}>
              {aboutSubtitle}
            </h2>

            <div className="h-px bg-white/8 mb-8" />

            <div className="text-white/60 mb-8 space-y-4" style={{ fontSize: '16px', lineHeight: 1.6 }}>
              <p>{h.aboutP1}</p>
              <p>{h.aboutP2}</p>
              <p className="text-white/80"><strong className="text-white font-medium">{h.aboutP6}</strong></p>
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="https://innobox.cl/wp-content/uploads/2025/05/Presentacion-Innobox-2025_v1.pdf"
                download="Transformación Digital"
                className="group relative inline-flex items-center overflow-hidden"
                style={{
                  border: '1px solid rgba(255,255,255,0.2)',
                  padding: '11px 22px',
                  fontSize: '13px',
                  color: '#ffffff',
                  letterSpacing: '0.02em',
                  transition: 'all 300ms ease-out'
                }}>
                <span
                  className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                  style={{ zIndex: 0 }} />
                
                <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                  {ctaPresentation}
                </span>
              </a>
              <Link
                href="/about"
                className="group relative inline-flex items-center overflow-hidden"
                style={{
                  border: '1px solid #abd233',
                  padding: '11px 22px',
                  fontSize: '13px',
                  color: '#abd233',
                  letterSpacing: '0.02em',
                  transition: 'all 300ms ease-out'
                }}>
                <span
                  className="absolute inset-0 bg-[#abd233] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                  style={{ zIndex: 0 }} />
                
                <span className="relative z-10 transition-colors duration-300 group-hover:text-[#141618]">
                  {ctaMore}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default AboutSection;